<template>
  <div class="serial-code-create">
    <div class="dialog-content">
      <div class="mb-x2" layout layout-align="space-between-start">
        <el-button type="default" @click="changeTab('serial-code-table')"
          ><i class="mdi mdi-keyboard-backspace"></i>戻る</el-button
        >
      </div>
      <!-- 対象メッセージ-->
      <div class="input-container">
        <div class="label">対象メッセージ</div>
        <el-select class="mb-x2" v-model="messageId" placeholder="対象メッセージ" :disabled="isEdit">
          <el-option
            v-for="(message, index) in messages"
            :value="message.id"
            :label="message.name"
            :key="index"
          ></el-option>
        </el-select>
      </div>
      <div class="message message-info mb-x2">
        シリアルコードを入力してください。<br />複数入力する場合、1シリアルコード１行となるよう改行してください。最大文字数は256文字になります。
      </div>
      <!-- シリアルコード-->
      <div class="input-container mb-x2">
        <el-input
          class="body-textarea"
          v-model="serialCode"
          type="textarea"
          placeholder="シリアルコードを入力"
          :class="{ success: $v.serialCode.required && !$v.serialCode.$invalid }"
        ></el-input>
        <div class="input-invalid-text" v-if="maxSerialCodeError">
          一度にインポートできる件数（10,000件）を超えています。
        </div>
      </div>
    </div>
    <div class="dialog-footer" slot="footer">
      <div layout layout-align="space-between-start">
        <div></div>
        <el-button type="primary" :disabled="disabled" @click="saveSerialCode"
          ><i class="mdi mdi-content-save"></i>保存</el-button
        >
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

:deep(.body-textarea) {
  textarea {
    height: 360px;
  }
}

:deep(.label) {
  position: relative;
  margin: 0 0 $spacing-unit;
  font-size: $font-large-size;

  .sub {
    position: absolute;
    top: 0;
    right: 0;
    color: $sub-text-color;
  }
}
</style>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'
import { required } from 'vuelidate/lib/validators'

import api from '@/client/core/api'
import { useTwitterInstantwinMessageStore } from '@/client/store/useTwitterInstantwinMessageStore'
import { useTwitterInstantwinSerialCodeStore } from '@/client/store/useTwitterInstantwinSerialCodeStore'

export default defineComponent({
  data() {
    return {
      serialCode: '',
      messageId: '' as number | '',
      isSubmit: false,
      isSubmited: false
    }
  },
  props: {
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState(useTwitterInstantwinSerialCodeStore, {
      currentMessageId: state => state.messageId,
      campaignId: state => state.campaignId
    }),
    ...mapState(useTwitterInstantwinMessageStore, {
      messages: state => state.messages.filter(message => message.messageType === 'win')
    }),
    isFormChanged() {
      return Boolean(this.serialCode.trim())
    },
    maxSerialCodeError() {
      if (!this.serialCode.trim()) {
        return false
      }

      return this.serialCode.trim().split('\n').length > 10000
    },
    disabled() {
      return this.$v.$invalid || !this.serialCode.trim() || this.maxSerialCodeError || this.isSubmit || !this.messageId
    }
  },
  watch: {
    isFormChanged(value) {
      this.$emit('update:isFormChanged', value)
    }
  },
  created() {
    if (this.isEdit) {
      this.messageId = this.currentMessageId
    }
  },
  methods: {
    ...mapActions(useTwitterInstantwinSerialCodeStore, ['fetchSerialCodeList']),
    ...mapActions(useTwitterInstantwinMessageStore, ['fetchInstantwinMessages']),

    /**
     * Change screen
     * @param {string} tab
     */
    changeTab(tab: string) {
      if (this.isFormChanged && !this.isSubmited) {
        const message = '編集内容が保存されていません。ダイアログを閉じてよろしいですか?'
        const confirm = window.confirm(message)

        if (!confirm) {
          return
        }
      }

      this.$emit('change_tab', tab)
    },

    /**
     * Save serial code
     */
    saveSerialCode() {
      if (this.isSubmit) return

      const serialCodes = this.serialCode.split('\n').filter(code => code)

      this.postButton({
        campaignId: this.campaignId,
        serialCodes,
        messageId: this.messageId
      })
    },

    /**
     * シリアルコードの新規作成
     * @param {Object} params 送信データ
     */
    async postButton(params: any) {
      this.isSubmit = true

      if (params.serialCodes.some(code => code.length > 256)) {
        this.isSubmit = false

        this.$notify({
          title: '最大文字数を超えるシリアルコードが含まれています。',
          message: '',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      const response = await api.post(`/twitter_instantwin_serial_codes`, params)

      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (response.data) {
        let title = `${response.data.savedCount}件シリアルコードを保存しました。`

        if (response.data.duplicatedCount) {
          title = `${response.data.savedCount}件シリアルコードを保存しました。(重複${response.data.duplicatedCount}件)`
        }

        this.$notify({
          title,
          message: '',
          customClass: 'success',
          duration: 5000
        })

        this.isSubmited = true

        this.changeTab('serial-code-table')
        await this.fetchSerialCodeList()
        await this.fetchInstantwinMessages()
        return
      }

      this.$notify({
        title: 'シリアルコードの保存に失敗しました。',
        message: '恐れ入りますが、時間をおいて再度お試しください。',
        customClass: 'danger',
        duration: 5000
      })
    }
  },
  validations: {
    serialCode: {
      required
    }
  }
})
</script>
